'use client';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import { useColorScheme } from '@mui/material/styles';

import { Iconify } from '../../iconify';
import { NavOptions } from './nav-options';
import { Scrollbar } from '../../scrollbar';
import { useSettingsContext } from '../context';
import { useThemeTransition } from '../theme-transition';
import { FullScreenButton } from './fullscreen-button';
import { AccountOptions } from './account-options';
import type { SettingsDrawerProps } from '../types';

export function SettingsDrawer({
  sx,
  hideCompact,
  hideNavColor,
  hideNavLayout,
  hideDirection,
  hideColorScheme,
}: SettingsDrawerProps) {
  const settings = useSettingsContext();

  const { mode, setMode } = useColorScheme();
  const { requestThemeTransition, isTransitioning } = useThemeTransition();

  const renderHead = (
    <Box display="flex" alignItems="center" sx={{ py: 2, pr: 1, pl: 2.5 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Settings
      </Typography>

      <FullScreenButton />

      <Tooltip title="Reset">
        <IconButton
          onClick={() => {
            settings.onReset();
            requestThemeTransition('dark');
          }}
        >
          <Badge color="error" variant="dot" invisible={!settings.canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Close">
        <IconButton onClick={settings.onCloseDrawer}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const handleModeToggle = () => {
    if (isTransitioning) return;
    const targetMode = mode === 'light' ? 'dark' : 'light';
    requestThemeTransition(targetMode);
  };

  const renderMode = (
    <Box>
      {/* <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.primary' }}>
        Theme
      </Typography> */}
      <Box
        onClick={handleModeToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleModeToggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isTransitioning ? 'wait' : 'pointer',
          opacity: isTransitioning ? 0.7 : 1,
          borderRadius: '14px',
          position: 'relative',
          width: '100%',
          height: 44,
          bgcolor: 'var(--bg-active)',
          border: '1px solid var(--line-strong)',
          transition: 'background-color 0.2s, border-color 0.2s',
          '&:hover': {
            bgcolor: 'var(--bg-hover)',
          },
        }}
      >
        {/* Thumb - slides left (dark) or right (light) */}
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            left: mode === 'dark' ? 4 : 'calc(50% + 2px)',
            width: 'calc(50% - 8px)',
            height: 36,
            borderRadius: '10px',
            bgcolor: 'var(--accent-text)',
            border: '1px solid var(--accent-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'left 0.25s ease',
            zIndex: 1,
          }}
        >
          <Iconify
            icon={mode === 'dark' ? 'solar:moon-bold' : 'solar:sun-bold'}
            width={20}
            sx={{ color: 'var(--bg-canvas)' }}
          />
        </Box>
        {/* Dark mode side (left) – moon visible when light mode is selected (inactive) */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
          }}
        >
          {mode === 'light' && (
            <Iconify
              icon="solar:moon-bold"
              width={22}
              sx={{ color: 'text.disabled', opacity: 0.7 }}
            />
          )}
        </Box>
        {/* Light mode side (right) – sun visible when dark mode is selected (inactive) */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
          }}
        >
          {mode === 'dark' && (
            <Iconify
              icon="solar:sun-bold"
              width={22}
              sx={{ color: 'text.disabled', opacity: 0.7 }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderToggleRow = (label, icon, selected, onChange, tooltip?: string, disabled?: boolean) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Iconify icon={icon} width={20} sx={{ color: 'text.primary', flexShrink: 0 }} />
        {tooltip ? (
          <Tooltip arrow title={tooltip} slotProps={{ tooltip: { sx: { maxWidth: 240 } } }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        )}
      </Box>
      <Switch
        size="small"
        checked={selected}
        onChange={(_, checked) => onChange(checked)}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );

  const renderRTL = renderToggleRow(
    'Right to left',
    'mdi:format-align-right',
    settings.direction === 'rtl',
    (checked) => settings.onUpdateField('direction', checked ? 'rtl' : 'ltr')
  );

  const renderCompact = renderToggleRow(
    'Compact',
    'mdi:arrow-expand-horizontal',
    settings.compactLayout,
    (checked) => settings.onUpdateField('compactLayout', checked),
    'Dashboard only and available at large resolutions > 1600px (xl)'
  );

  const renderNav = (
    <NavOptions
      value={{
        color: settings.navColor,
        layout: settings.navLayout,
      }}
      onClickOption={{
        color: (newValue) => settings.onUpdateField('navColor', newValue),
        layout: (newValue) => settings.onUpdateField('navLayout', newValue),
      }}
      options={{
        colors: [],
        layouts: ['vertical', 'mini'],
      }}
      hideNavColor={hideNavColor}
      hideNavLayout={hideNavLayout}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={settings.openDrawer}
      onClose={settings.onCloseDrawer}
      variant="temporary"
      slotProps={{
        backdrop: {
          invisible: true,
          sx: { cursor: 'pointer' },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100vh',
          width: 440,
          zIndex: 1001,
        },
      }}
      sx={{
        width: 440,
        '& .MuiDrawer-paper': {
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100vh',
          width: 440,
        },
        ...sx,
      }}
    >
      {renderHead}

      <Scrollbar>
        <Stack spacing={1} sx={{ px: 2.5, pb: 5 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: '14px',
              bgcolor: 'var(--bg-hover)',
              border: '1px solid var(--line-strong)',
            }}
          >
            <Stack spacing={2}>
              {!hideColorScheme && renderMode}
              {/* {!hideContrast && renderContrast} */}
              {!hideDirection && renderRTL}
              {!hideCompact && renderCompact}
              {/* {renderTypographyMode} */}
            </Stack>
          </Box>
          {!(hideNavLayout && hideNavColor) && (
            <Box
              sx={{
                p: 2,
                borderRadius: '14px',
                bgcolor: 'var(--bg-hover)',
                border: '1px solid var(--line-strong)',
              }}
            >
              {renderNav}
            </Box>
          )}
          <AccountOptions />
          {/* {!hidePresets && renderPresets} */}
          {/* {!hideThemePalette && renderThemePalette} */}
          {/* {!hideFont && renderFont} */}
        </Stack>
      </Scrollbar >
    </Drawer >
  );
}
